import React, { useEffect, useMemo, useRef } from 'react';
import { Center } from '@mantine/core';
import { useSearchParams } from 'react-router-dom';
import { useNavigation } from '@refinedev/core';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { CalendarPlatform } from 'src/meetings/models';
import PageLoader from 'src/UI/loader/PageLoader';
import { useCreateRemoteDocsConnectionMutation } from 'src/secret-menu/sharepoint/hooks/useCreateRemoteDocsConnectionMutation';

type Props = {
  platform: CalendarPlatform;
};

export const DocsOAuthRedirectPage = ({ platform }: Props) => {
  const [searchParams] = useSearchParams();
  const { createRemoteDocsConnection } = useCreateRemoteDocsConnectionMutation();
  const { push } = useNavigation();

  const code = useMemo(() => searchParams.get('code') ?? '', [searchParams]);
  const state = useMemo(() => searchParams.get('state') ?? '', [searchParams]);
  const error = useMemo(() => searchParams.get('error') ?? '', [searchParams]);

  const hasRunRef = useRef(false);

  useEffect(() => {
    if (error && !hasRunRef.current) {
      hasRunRef.current = true;
      notifyAndLogError(
        'The connection was not successful',
        `There was an error connecting to your account. The provider returned the error: ${error}. Please try again or contact support if the issue persists.`,
        new Error(`OAuth error: ${error}`),
      );
      push('/secret-menu/sharepoint');
      return;
    }
  }, [error, push]);

  useEffect(() => {
    if (!code || hasRunRef.current) return;
    hasRunRef.current = true;
    (async () => {
      try {
        await createRemoteDocsConnection({ authCode: code, platform, state });
        push('/secret-menu/sharepoint');
      } catch (error) {
        notifyAndLogError('Failed to connect', 'Something went wrong. Please try again.', error);
        push('/secret-menu/sharepoint');
      }
    })();
  }, [code, createRemoteDocsConnection, push]);

  return (
    <Center h="calc(100vh - 72px)">
      <PageLoader />
    </Center>
  );
};
